// Classifies a completed/failed attempt into one taxonomy category. Order is
// specific -> generic exactly as the phase file's architecture section
// states: transport -> result.subtype/is_error -> rate-limit (result.result +
// stderrTail ONLY, never assistant/tool text) -> auth -> permission (hook
// deny evidence + no result) -> task_failed fallback.
import { detectRateLimitWakeAt } from "./rate-limit-detector.js";

export type ErrorCategory =
  | "quota"
  | "rate_limit"
  | "auth_expired"
  | "network"
  | "permission"
  | "task_failed"
  | "contract_error";

export interface ClassifierInput {
  spawnError?: { code?: string; message: string };
  exitCode?: number | null;
  signal?: string | null;
  timedOut?: boolean;
  startupFailed?: boolean;
  resultEvent?: {
    subtype?: string;
    is_error?: boolean;
    result?: string;
  };
  stderrTail?: string;
  permissionDenials?: Array<{ tool_name?: string; reason?: string }>;
  hookDenyEvidence?: boolean;
}

export interface ClassificationResult {
  category: ErrorCategory;
  reason: string;
  wakeAt?: Date | null;
}

const RATE_LIMIT_PHRASES = /rate limit|rate_limit|too many requests|429\b.*rate/i;
const QUOTA_PHRASES =
  /usage limit exceeded|quota exceeded|credit balance too low|insufficient_quota/i;
const AUTH_PHRASES = /login expired|please run \/login|oauth token expired|invalid api key/i;

function scanRateLimitOrQuota(text: string): "quota" | "rate_limit" | undefined {
  if (QUOTA_PHRASES.test(text)) return "quota";
  if (RATE_LIMIT_PHRASES.test(text)) return "rate_limit";
  return undefined;
}

export function classifyAttemptError(input: ClassifierInput): ClassificationResult {
  // 1. Transport (ENOENT/killed by us without a result).
  if (input.spawnError) {
    return { category: "network", reason: `transport_error:${input.spawnError.code ?? "unknown"}` };
  }
  if (input.startupFailed) {
    return { category: "network", reason: "startup_failed_no_init_event" };
  }

  // 2. result.subtype / is_error (co-occurring is_error+success = error, D03).
  const resultText = input.resultEvent?.result ?? "";
  const stderrTail = input.stderrTail ?? "";
  const hasErrorResult = input.resultEvent?.is_error === true;

  // H3 (audit 2026-09-28): a clean success (is_error not true, subtype
  // success) is never a quota/rate/auth failure, whatever its prose says —
  // "I tested rate limit handling" put a finished attempt into quota_sleep.
  // Providers flag a real usage limit with is_error:true (D03), so it still
  // reaches the scan below.
  const cleanSuccess =
    input.resultEvent !== undefined && !hasErrorResult && input.resultEvent.subtype === "success";

  // 3. Rate-limit / quota — scans ONLY result.result + stderrTail, never
  // assistant/tool text (test 27's critical rule).
  const scanText = cleanSuccess ? "" : `${resultText}\n${stderrTail}`;
  const rateOrQuota = scanRateLimitOrQuota(scanText);
  if (rateOrQuota === "quota") {
    return { category: "quota", reason: "quota_phrase_in_result_or_stderr" };
  }
  if (rateOrQuota === "rate_limit") {
    const detection = detectRateLimitWakeAt(scanText);
    return {
      category: "rate_limit",
      reason: "rate_limit_phrase_in_result_or_stderr",
      wakeAt: detection.wakeAt,
    };
  }

  // 4. Auth.
  if (AUTH_PHRASES.test(scanText)) {
    return { category: "auth_expired", reason: "auth_expired_phrase_in_result_or_stderr" };
  }

  // Error result without a more specific category above -> task_failed
  // (covers is_error:true + subtype:success per D03, and other failure
  // subtypes not otherwise classified).
  if (hasErrorResult) {
    return { category: "task_failed", reason: "result_is_error_true" };
  }

  // 5. Permission — hook deny evidence present AND no result event.
  if (input.hookDenyEvidence === true && !input.resultEvent) {
    return { category: "permission", reason: "hook_deny_evidence_no_result_event" };
  }
  if ((input.permissionDenials?.length ?? 0) > 0 && !input.resultEvent) {
    return { category: "permission", reason: "permission_denials_no_result_event" };
  }

  // 6. Fallback.
  return { category: "task_failed", reason: "generic_failure_fallback" };
}
